'use client';
import { useEffect, useRef } from 'react';

export default function CustomCursor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(undefined);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < 768 || window.matchMedia('(hover: none)').matches) return;

    const canvas = canvasRef.current;
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!canvas || !dot || !ring) return;

    const ctx = canvas.getContext('2d')!;

    function resize() {
      canvas!.width = window.innerWidth;
      canvas!.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    // --- State ---
    const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ringState = { x: mouse.x, y: mouse.y, vx: 0, vy: 0 };
    const particles: {
      x: number; y: number; vx: number; vy: number;
      life: number; size: number; hue: number;
    }[] = [];
    const shockwaves: { x: number; y: number; r: number; life: number }[] = [];

    let isHover = false;
    let isWork = false;
    let isClick = false;
    let lastMx = mouse.x, lastMy = mouse.y;

    // --- Mouse move + magnetic snap ---
    const onMove = (e: MouseEvent) => {
      let mx = e.clientX, my = e.clientY;

      // Check for work items specifically first
      const workTarget = (e.target as HTMLElement).closest('[data-cursor="work"]');
      isWork = !!workTarget;

      // Find nearest interactive element for magnetic pull
      const targets = document.querySelectorAll<HTMLElement>('a, button, [role="button"]');
      let closestDist = 9999;
      let closestEl: { cx: number; cy: number } | null = null;

      targets.forEach(el => {
        const br = el.getBoundingClientRect();
        const cx = br.left + br.width / 2;
        const cy = br.top + br.height / 2;
        const d = Math.hypot(e.clientX - cx, e.clientY - cy);
        if (d < closestDist) { closestDist = d; closestEl = { cx, cy }; }
      });

      if (closestEl && closestDist < 80) {
        const el = closestEl as { cx: number; cy: number };
        const pull = Math.max(0, 1 - closestDist / 80);
        mx += (el.cx - e.clientX) * pull * 0.38;
        my += (el.cy - e.clientY) * pull * 0.38;
        isHover = closestDist < 52;
      } else {
        isHover = false;
      }

      const speed = Math.hypot(mx - lastMx, my - lastMy);

      // Spawn particles proportional to speed
      if (speed > 1.5) {
        const count = Math.min(3, Math.floor(speed / 6));
        for (let i = 0; i <= count; i++) {
          particles.push({
            x: lastMx + (mx - lastMx) * (i / count),
            y: lastMy + (my - lastMy) * (i / count),
            vx: (lastMx - mx) * 0.07 + (Math.random() - .5) * 0.8,
            vy: (lastMy - my) * 0.07 + (Math.random() - .5) * 0.8,
            life: 1,
            size: Math.min(4, 1.2 + speed * 0.07),
            hue: 240 + Math.random() * 20,   // purple band
          });
        }
      }

      lastMx = mx; lastMy = my;
      mouse.x = mx; mouse.y = my;

      // Update dot position
      dot.style.transform = `translate3d(${mx}px, ${my}px, 0) translate(-50%, -50%)`;
      
      // Update cursor states
      if (isHover) {
        dot.classList.add('cursor-hover');
        ring.classList.add('cursor-hover');
      } else {
        dot.classList.remove('cursor-hover');
        ring.classList.remove('cursor-hover');
      }

      if (isWork) {
        dot.classList.add('cursor-work');
        ring.classList.add('cursor-work');
      } else {
        dot.classList.remove('cursor-work');
        ring.classList.remove('cursor-work');
      }
    };

    // --- Click shockwave ---
    const onDown = () => {
      isClick = true;
      shockwaves.push({ x: mouse.x, y: mouse.y, r: 0, life: 1 });
      dot.classList.add('cursor-click');
      ring.classList.add('cursor-click');
    };
    const onUp = () => { 
      isClick = false; 
      dot.classList.remove('cursor-click');
      ring.classList.remove('cursor-click');
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);

    // --- Lerp helper ---
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    // --- Render loop ---
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Spring-physics ring position
      ringState.vx += (mouse.x - ringState.x) * 0.12;
      ringState.vy += (mouse.y - ringState.y) * 0.12;
      ringState.vx *= 0.70;
      ringState.vy *= 0.70;
      ringState.x += ringState.vx;
      ringState.y += ringState.vy;

      // Update ring div position
      ring.style.transform = `translate3d(${ringState.x}px, ${ringState.y}px, 0) translate(-50%, -50%)`;

      // Particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.86;
        p.vy *= 0.86;
        p.life -= 0.048;
        if (p.life <= 0) { particles.splice(i, 1); continue; }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue},81%,69%,${p.life * 0.65})`;
        ctx.fill();
      }

      // Shockwaves
      for (let i = shockwaves.length - 1; i >= 0; i--) {
        const s = shockwaves[i];
        s.r += 5.5;
        s.life -= 0.055;
        if (s.life <= 0) { shockwaves.splice(i, 1); continue; }

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(123, 111, 240,${s.life * 0.55})`;
        ctx.lineWidth = 2 * s.life;
        ctx.stroke();
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <>
      <canvas ref={canvasRef} className="dsl-cursor-canvas" aria-hidden="true" />
      <div ref={dotRef} className="cursor-dot" aria-hidden="true" />
      <div ref={ringRef} className="cursor-ring" aria-hidden="true">
        <span className="cursor-work-label">
           <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
             <line x1="7" y1="17" x2="17" y2="7"></line>
             <polyline points="7 7 17 7 17 17"></polyline>
           </svg>
           View Work
        </span>
      </div>
    </>
  );
}